<template>
  <div v-if="isLoading" class="flex items-center justify-center h-40 rounded-2xl">
    <i v-html="$icons.loading"></i>
  </div>
  <div v-else class="overflow-hidden rounded-2xl shadow-lg">
    <div class="overflow-x-auto overflow-y-auto">
      <table class="bg-ontick-graphite-500 divide-ontick-steel-500 min-w-full divide-y">
        <thead class="bg-ontick-steel-500">
          <tr>
            <th
              v-for="(headerStrat, index) in headerStrategy"
              :key="headerStrat.key"
              :class="{
                'w-20': index === 0,
              }"
            >
              <button
                :class="{ 'sr-only': headerStrat.hidden }"
                :disabled="!headerStrat.sortable"
                @click="sort(headerStrat.key)"
              >
                <span>
                  {{ headerStrat.label }}
                </span>
                <span v-if="sort_key == headerStrat.key">
                  {{ sortIcon }}
                </span>
              </button>
            </th>
          </tr>
        </thead>

        <tbody class="divide-ontick-steel-500 divide-y">
          <tr v-if="!getTradeIdeas.length">
            <td colspan="9999">
              <div class="h-96 w-full flex items-center justify-center">
                <p class="text-base font-medium text-gray-300">Nenhuma estratégia encontrada</p>
              </div>
            </td>
          </tr>

          <!-- ================================================== -->

          <template v-for="strat in sortedData">
            <!-- ================================================== -->

            <!-- #region Tabela de estratégias -->
            <tr @click.stop="expand(strat)" class="cursor-pointer">
              <td>
                <div class="flex justify-center">
                  <ontick-switch-component
                    id="switch-trade-idea"
                    :active="hasTradeIdeaActive(strat)"
                    @switch="switchStrategy(strat)"
                  />
                </div>
                <!-- <p>{{ expandedStrategies }}</p>
              <p>{{ strat.content }}</p> -->
              </td>
              <td>
                <div class="flex items-center gap-2">
                  <img :src="strat.partner_badge" :alt="strat.partner_name" class="w-5 h-5" />
                  <p class="text-xl font-semibold">{{ strat.partner_name }}</p>
                </div>
              </td>
              <td>
                <p class="font-medium">{{ strat.strategy_name }}</p>
              </td>
              <td>
                <p class="font-medium">{{ strat.strategy_analyst }}</p>
              </td>
              <td>
                <div class="flex flex-col gap-1">
                  <p v-if="strat.total_volume" class="leading-none font-medium">
                    {{ Number(strat.total_volume).toLocaleString("pt-BR") }}&nbsp;<span
                      class="font-normal leading-none text-sm text-gray-500"
                      >ações</span
                    >
                  </p>
                  <span v-else class="text-gray-500 text-sm font-semibold">-</span>
                  <span class="text-sm leading-none font-medium text-gray-500"
                    >{{ Number(strat.total_allocation).toLocaleString("pt-BR") }}%</span
                  >
                </div>
              </td>
              <td>
                <div class="flex items-center gap-2">
                  <i v-html="$icons.play_green" v-if="strat.total_pnl > 0"></i>
                  <i v-html="$icons.play_red" v-else-if="strat.total_pnl < 0"></i>
                  <i v-else>
                    <div class="h-3 w-3 rounded-full bg-gray-500"></div>
                  </i>
                  <span v-tippy="{ theme: 'ontick', arrow: true }" :content="strat.total_pnl" class="font-medium">{{
                    strat.total_pnl | currency
                  }}</span>
                </div>
              </td>
            </tr>
            <!-- #endregion Tabela de estratégias -->

            <!-- ================================================== -->

            <!-- #region Sub-Tabela de Trade Ideas -->
            <tr v-if="isExpanded(strat.auth_strat_id)" class="h-4 w-full no-divide border-none">
              <td
                colspan="9999"
                class="!px-0 !py-0 bg-gradient-to-b from-ontick-graphite-500 to-ontick-carbon-500"
              ></td>
            </tr>
            <tr v-if="isExpanded(strat.auth_strat_id)" class="bg-ontick-carbon-500 !px-0 no-divide border border-none">
              <td colspan="9999" class="!px-0 !py-0">
                <!-- ================================================== -->
                <table id="table_tradeidea" class="w-full">
                  <thead>
                    <tr>
                      <th
                        v-for="headerTi in headerTradeIdea"
                        :key="headerTi.key"
                        class="!text-xs font-semibold text-gray-300"
                      >
                        <span v-if="!headerTi.hidden">
                          {{ headerTi.label }}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="!text-sm !font-normal !bg-transparent">
                    <!-- Mensagem padrão caso não possua trade ideas -->
                    <tr v-if="!strat.trade_ideas.length">
                      <td colspan="9999">
                        <div class="h-20 w-full flex items-center justify-center">
                          <p class="text-base font-medium text-gray-300">Nenhuma trade idea encontrada</p>
                        </div>
                      </td>
                    </tr>

                    <!-- ================================================== -->

                    <!-- Conteúdo da sub-tabela de trade ideas -->
                    <tr v-for="ti in strat.trade_ideas" :key="ti.ti_id">
                      <!-- Ligar/desligar -->
                      <td>
                        <div class="flex justify-center">
                          <ontick-switch-component
                            id="switch-trade-idea"
                            :active="ti.ti_status === 'ACTIVE'"
                            @switch="switchTradeIdea(ti)"
                          />
                        </div>
                      </td>

                      <!-- ID -->
                      <td>
                        <p class="text-gray-300">{{ ti.ti_id }}</p>
                      </td>

                      <!-- Status -->
                      <td>
                        <p class="text-gray-300">{{ tradeIdeasTypes.status(ti.ti_status) }}</p>
                      </td>

                      <!-- Operação -->
                      <td>
                        <div class="flex flex-col items-start gap-1">
                          <div class="flex items-center gap-2">
                            <!-- Ícone do ativo -->
                            <img :src="ti.ti_symbol_badge" :alt="ti.ti_symbol" class="w-5 h-5 rounded-sm" />

                            <!-- Ativo -->
                            <span
                              class="text-xs font-medium bg-ontick-blue-900 rounded-sm flex items-center justify-center w-[54px] h-5"
                            >
                              {{ ti.ti_symbol }}
                            </span>

                            <!-- Compra (C) / Venda (V) -->
                            <span
                              v-tippy="{ theme: 'ontick', arrow: true }"
                              :content="ti.ti_direction === 'BUY' ? 'Compra' : 'Venda'"
                              class="w-5 h-5 flex items-center justify-center rounded-sm bg-ontick-yellow-800 text-ontick-graphite-500 text-xs font-bold"
                            >
                              {{ ti.ti_direction === "BUY" ? "C" : "V" }}
                            </span>
                          </div>
                          <span class="text-xs font-normal text-gray-300"
                            >Atual: {{ ti.ti_symbol_price | currency }}</span
                          >
                        </div>
                      </td>

                      <!-- Entrada -->
                      <td>
                        <p
                          v-if="ti.to_entry_price"
                          v-tippy="{ theme: 'ontick', arrow: true }"
                          :content="ti.to_entry_price"
                          class="text-gray-300"
                        >
                          {{ ti.to_entry_price | currency }}
                        </p>
                        <span v-else class="text-gray-500 text-sm font-semibold">-</span>
                      </td>

                      <!-- Targets -->
                      <td>
                        <div class="flex flex-col gap-1">
                          <p class="whitespace-nowrap space-x-1">
                            <span class="text-ontick-green-600 font-semibold">TP:</span>
                            <!-- Financeiro -->
                            <span
                              v-if="ti.ti_take_profit_price"
                              v-tippy="{ theme: 'ontick', arrow: true }"
                              :content="ti.ti_take_profit_price"
                              class="text-gray-300"
                            >
                              {{ ti.ti_take_profit_price | currency }}
                            </span>
                            <span v-else class="text-gray-500 text-sm font-semibold">-</span>

                            <!-- Percentual -->
                            <span
                              v-if="ti.ti_take_profit_percent"
                              v-tippy="{ theme: 'ontick', arrow: true }"
                              :content="ti.ti_take_profit_percent"
                              class="text-xs text-gray-500"
                              >({{ Number(ti.ti_take_profit_percent).toLocaleString("pt-BR") }}%)</span
                            >
                          </p>
                          <p class="whitespace-nowrap space-x-1">
                            <span class="text-ontick-red-600 font-semibold">SL:</span>
                            <!-- Financeiro -->
                            <span
                              v-if="ti.ti_stop_loss_price"
                              v-tippy="{ theme: 'ontick', arrow: true }"
                              :content="ti.ti_stop_loss_price"
                              class="text-gray-300"
                              >{{ ti.ti_stop_loss_price | currency }}</span
                            >
                            <span v-else class="text-gray-500 text-sm font-semibold">-</span>

                            <!-- Percentual -->
                            <span
                              v-if="ti.ti_stop_loss_percent"
                              v-tippy="{ theme: 'ontick', arrow: true }"
                              :content="ti.ti_stop_loss_percent"
                              class="text-xs text-gray-500"
                              >({{ Number(ti.ti_stop_loss_percent).toLocaleString("pt-BR") }}%)</span
                            >
                          </p>
                        </div>
                      </td>

                      <!-- Alocação -->
                      <td>
                        <div class="flex flex-col gap-1">
                          <p v-if="ti.to_volume_1" class="text-gray-300">
                            {{ Number(ti.to_volume_1).toLocaleString("pt-BR") }}&nbsp;<span
                              class="text-sm text-gray-500"
                              >ações</span
                            >
                          </p>
                          <span v-else class="text-gray-500 text-sm font-semibold">-</span>
                          <span class="text-sm text-gray-500"
                            >{{ Number(ti.ti_allocation_percent).toLocaleString("pt-BR") }}%</span
                          >
                        </div>
                      </td>

                      <!-- Resultado -->
                      <td>
                        <div v-if="ti.to_pnl != null" class="flex items-center gap-2">
                          <i v-html="$icons.play_green" v-if="ti.to_pnl > 0"></i>
                          <i v-html="$icons.play_red" v-else-if="ti.to_pnl < 0"></i>
                          <i v-else>
                            <div class="h-3 w-3 rounded-full bg-gray-500"></div>
                          </i>
                          <span v-tippy="{ theme: 'ontick', arrow: true }" :content="ti.to_pnl" class="text-gray-300">{{
                            ti.to_pnl | currency
                          }}</span>
                        </div>
                        <span v-else class="text-gray-500 text-sm font-semibold">-</span>
                      </td>

                      <!-- Horários -->
                      <td>
                        <div v-if="true" class="flex flex-col gap-1">
                          <div class="flex items-center">
                            <span class="text-gray-500">Início:&nbsp;</span>
                            <span
                              v-if="ti.ti_start_datetime"
                              class="font-mono whitespace-nowrap tracking-tighter text-sm text-gray-300"
                            >
                              {{ moment(ti.ti_start_datetime).format("YYYY-MM-DD HH:mm:ss") }}
                            </span>
                            <span v-else class="text-gray-500 text-sm font-semibold">-</span>
                          </div>
                          <div class="flex items-center">
                            <span class="text-gray-500">Fim:&nbsp;&nbsp;&nbsp;&nbsp;</span>
                            <span
                              v-if="ti.ti_user_end_datetime"
                              class="font-mono whitespace-nowrap tracking-tighter text-sm text-gray-300"
                            >
                              {{ moment(ti.ti_user_end_datetime).format("YYYY-MM-DD HH:mm:ss") }}
                            </span>
                            <span v-else class="text-gray-500 text-sm font-semibold">-</span>
                          </div>
                        </div>
                      </td>

                      <!-- Detalhes -->
                      <td>
                        <button
                          @click="
                            $emit('details', {
                              ...ti,
                              auth_strat_id: strat.auth_strat_id,
                              partner_id: strat.partner_id,
                              partner_name: strat.partner_name,
                              strategy_name: strat.strategy_name,
                              strategy_analyst: strat.strategy_analyst,
                              partner_badge: strat.partner_badge,
                              total_allocation: strat.total_allocation,
                              total_volume: strat.total_volume,
                              total_pnl: strat.total_pnl,
                            })
                          "
                        >
                          <svg
                            width="17"
                            height="18"
                            viewBox="0 0 17 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4.01784 13.6062H10.3199V12.2312H4.01784V13.6062ZM4.01784 9.6875H13.322V8.3125H4.01784V9.6875ZM4.01784 5.76875H13.322V4.39375H4.01784V5.76875ZM1.79492 17.25C1.42826 17.25 1.10742 17.1125 0.832422 16.8375C0.557422 16.5625 0.419922 16.2417 0.419922 15.875V2.125C0.419922 1.75833 0.557422 1.4375 0.832422 1.1625C1.10742 0.8875 1.42826 0.75 1.79492 0.75H15.5449C15.9116 0.75 16.2324 0.8875 16.5074 1.1625C16.7824 1.4375 16.9199 1.75833 16.9199 2.125V15.875C16.9199 16.2417 16.7824 16.5625 16.5074 16.8375C16.2324 17.1125 15.9116 17.25 15.5449 17.25H1.79492ZM1.79492 15.875H15.5449V2.125H1.79492V15.875ZM1.79492 2.125V15.875V2.125Z"
                              fill="#0694E6"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>

                    <!-- ================================================== -->
                  </tbody>
                </table>
                <!-- ================================================== -->
              </td>
            </tr>
            <!-- #endregion Sub-Tabela de Trade Ideas -->

            <!-- ================================================== -->
          </template>

          <!-- ================================================== -->
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import OntickSwitchComponent from "../../../shared/components/basic/ontick-switch.component.vue";
import OntickBrokerLogoComponent from "../../../shared/components/basic/ontick-broker-logo.component.vue";
import { tradeIdeasTypes } from "../utils-trade-ideas";

export default {
  name: "TableOngoing",

  components: {
    OntickSwitchComponent,
    OntickBrokerLogoComponent,
  },

  data() {
    return {
      tradeIdeasTypes,

      // Estratégias expandidas
      expandedStrategies: [],

      // Ordenação
      sort_order: "asc",
      sort_key: "auth_strat_id",

      // Cabeçalho da tabela de estratégias
      headerStrategy: [
        { label: "", key: "#", sortable: false },
        { label: "Parceiro", key: "parceiro", sortable: false },
        { label: "Estratégia", key: "estrategia", sortable: false },
        { label: "Analista responsável", key: "analista_responsavel", sortable: false },
        { label: "Alocação", key: "alocacao", sortable: false },
        { label: "", key: "info", sortable: false },
      ],

      headerTradeIdea: [
        { label: "", key: "#", sortable: false },
        { label: "ID", key: "id", sortable: false },
        { label: "Status", key: "status", sortable: false },
        { label: "Operação", key: "operacao", sortable: false },
        { label: "Entrada", key: "entrada", sortable: false },
        { label: "Targets", key: "targets", sortable: false },
        { label: "Alocação", key: "alocacao", sortable: false },
        { label: "Resultado", key: "resultado", sortable: false },
        { label: "Horários", key: "horarios", sortable: false },
        { label: "", key: "info", sortable: false },
      ],
    };
  },

  computed: {
    ...mapGetters({
      getTradeIdeas: "tradeIdeas/getTradeIdeas",
      isLoading: "tradeIdeas/isLoading",
    }),

    sortIcon() {
      const directions = {
        asc: "▲",
        desc: "▼",
      };
      // return directions[this.sortDirection] || "";
      return directions[this.sort_order] || "▲";
    },

    sortedData() {
      return this.getTradeIdeas.slice().sort((a, b) => {
        let modifier = 1;
        if (this.sort_order === "desc") modifier = -1;
        if (a[this.sort_key] < b[this.sort_key]) return -1 * modifier;
        if (a[this.sort_key] > b[this.sort_key]) return 1 * modifier;
        return 0;
      });
    },
  },

  methods: {
    ...mapActions({
      listTradeIdeas: "tradeIdeas/listTradeIdeas",
    }),

    sort(key) {
      this.sort_key = key;
      this.sort_order = this.sort_order == "asc" ? "desc" : "asc";
    },

    expand(strat) {
      const item = this.expandedStrategies.find((s) => s.auth_strat_id === strat.auth_strat_id);
      if (item) {
        this.expandedStrategies = this.expandedStrategies.filter((s) => s.auth_strat_id !== strat.auth_strat_id);
        return;
      }
      this.expandedStrategies.push(strat);
    },

    isExpanded(authStratId) {
      return this.expandedStrategies.some((s) => s.auth_strat_id === authStratId);
    },

    switchStrategy(strat) {
      this.$emit("stop-strategy", { strat });
    },

    switchTradeIdea(ti) {
      this.$emit("stop-trade-idea", { ti });
    },

    hasTradeIdeaActive(strat) {
      return strat.trade_ideas.some((ti) => ti.ti_status === "ACTIVE");
    },
  },

  created() {
    this.listTradeIdeas();
  },
};
</script>

<style lang="scss" scoped>
.no-divide {
  border-top-width: 0 !important;
}

th {
  @apply py-2 px-3 text-left;
  button {
    @apply text-sm font-semibold text-gray-300;
  }
}

th,
td {
  border-style: solid !important;
}

tbody {
  @apply text-base font-semibold bg-ontick-graphite-500 relative;

  tr {
    @apply relative;

    td {
      @apply px-3 py-6 text-gray-50;

      #table_tradeidea td {
        @apply px-3 py-4;
      }
    }
  }
}

.fade {
  &-enter,
  &-leave-to {
    @apply scale-125 opacity-0;
  }
  &-enter-active,
  &-leave-active {
    @apply transition-all duration-300 ease-in-out;
  }
}

/* width */
::-webkit-scrollbar {
  width: 4px;
  height: 3px;
}

/* Track */
::-webkit-scrollbar-track {
  background-color: #2d3748;
}

/* Handle */
::-webkit-scrollbar-thumb {
  @apply rounded-full;
  background-color: #0694e6;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
